import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, AlertTriangle, CheckCircle2, Clock3, Package, PauseCircle, Search } from "lucide-react";
import { ACTIVE_GAME_CATALOG, STATUS_REFRESH_INTERVAL_MS } from "../data/gameCatalog";
import {
  FallbackImage,
  ProductInfoBadges,
  STATUS_COLORS,
  STATUS_FILTERS,
  SeoMeta,
  SiteFooter,
  SiteNavbar,
  dataUrl,
  formatDateTime,
  formatOs,
  getStatusIndex,
  prefetchDataFile,
  preloadImage,
} from "../shared/site";
import "../index.css";

const STATUS_META = {
  all: {
    label: "All",
    className: "bg-gray-700/80 border-gray-500 text-white",
    icon: Package,
  },
  undetected: {
    label: "Undetected",
    className: "bg-green-600/15 border-green-500/60 text-green-300",
    icon: CheckCircle2,
  },
  updating: {
    label: "Updating",
    className: "bg-amber-500/15 border-amber-400/60 text-amber-300",
    icon: Clock3,
  },
  detected: {
    label: "Detected",
    className: "bg-red-600/15 border-red-500/60 text-red-300",
    icon: AlertTriangle,
  },
  frozen: {
    label: "Frozen",
    className: "bg-gray-500/15 border-gray-400/60 text-gray-300",
    icon: PauseCircle,
  },
};

function formatRefreshTime(date) {
  return date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "Loading";
}

function SearchBar({ value, onChange, resultCount }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3">
      <div className="relative flex-1">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search product, game, mode, Windows or price..."
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="status-search-field w-full pl-12 pr-5 py-3 rounded-lg border border-purple-400/20 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg shadow-md"
        />
      </div>
      <div className="text-gray-400 text-base md:text-lg text-center md:text-right shrink-0">
        {resultCount} result{resultCount > 1 ? "s" : ""}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.all;

  return (
    <span className={`status-pulse status-${status} inline-flex items-center justify-center gap-2 px-3.5 py-1.5 rounded-full border text-base font-bold ${meta.className}`}>
      <span className={`h-2 w-2 rounded-full ${STATUS_COLORS[status]?.replace("text-", "bg-") || "bg-white"}`} />
      {meta.label}
    </span>
  );
}

function SummaryCards({ stats, totalCount }) {
  const cards = [
    { key: "total", label: "Total products", value: totalCount, icon: Package, className: "border-purple-500/40 text-purple-300" },
    { key: "undetected", label: "Undetected", value: stats.undetected || 0, icon: CheckCircle2, className: "border-green-500/40 text-green-300" },
    { key: "updating", label: "Updating", value: stats.updating || 0, icon: Clock3, className: "border-amber-500/40 text-amber-300" },
    { key: "detected", label: "Detected", value: stats.detected || 0, icon: AlertTriangle, className: "border-red-500/40 text-red-300" },
    { key: "frozen", label: "Frozen", value: stats.frozen || 0, icon: PauseCircle, className: "border-gray-500/40 text-gray-300" },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <div key={card.key} className={`bg-gray-950/60 border ${card.className} rounded-lg p-4 md:p-5 shadow-lg`}>
            <div className="flex items-center justify-between gap-3">
              <p className="text-gray-300 text-base md:text-lg font-medium">{card.label}</p>
              <Icon size={22} className={card.className.split(" ").find((part) => part.startsWith("text-"))} />
            </div>
            <p className="text-3xl md:text-4xl font-extrabold text-white mt-2">{card.value}</p>
          </div>
        );
      })}
    </div>
  );
}

function StatusFilters({ selectedStatus, onSelect, stats, totalCount }) {
  return (
    <div className="flex flex-wrap gap-3">
      {STATUS_FILTERS.map((status) => {
        const meta = STATUS_META[status] || STATUS_META.all;
        const count = status === "all" ? totalCount : stats[status] || 0;
        const selected = selectedStatus === status;

        return (
          <button
            key={status}
            type="button"
            onClick={() => onSelect(status)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border font-bold transition ${
              selected ? "ring-2 ring-purple-400 scale-[1.02]" : "hover:border-purple-400/80"
            } ${meta.className}`}
          >
            {meta.label}
            <span className="rounded-full bg-black/30 px-2 py-0.5 text-xs">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

function ProductStatusGrid({ rows }) {
  return (
    <motion.div layout className="grid grid-cols-1 gap-2">
      <AnimatePresence>
        {rows.map((item) => (
          <motion.div
            key={`${item.gameTitle}-${item.name}-${item.link}`}
            layout
            initial={{ opacity: 0, scale: 0.94, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <Link
              to={item.link}
              onMouseEnter={() => {
                prefetchDataFile(item.dataFile);
                preloadImage(item.logo || item.gameImage);
              }}
              onFocus={() => {
                prefetchDataFile(item.dataFile);
                preloadImage(item.logo || item.gameImage);
              }}
              className="group grid gap-3 rounded-lg border border-purple-400/20 bg-gray-950/60 p-3 shadow-lg transition hover:border-purple-400/80 hover:bg-purple-950/20 hover:shadow-purple-500/20 md:grid-cols-[minmax(0,1.45fr)_minmax(13rem,0.9fr)_minmax(8rem,auto)_minmax(10rem,auto)] md:items-center"
            >
              <div className="flex min-w-0 items-center gap-3">
                <FallbackImage
                  src={item.logo}
                  alt={item.name}
                  label={item.name}
                  className="h-12 w-12 shrink-0 rounded-md border border-purple-400/20 bg-gray-900 object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold uppercase text-gray-500">{item.gameTitle}</p>
                  <h4 className="truncate text-xl font-extrabold leading-tight text-purple-200 group-hover:text-purple-100">
                    {item.name}
                  </h4>
                </div>
              </div>

              <div className="min-w-0">
                <ProductInfoBadges item={item} compact />
              </div>

              <div className="flex items-center justify-between gap-3 md:justify-end">
                <StatusBadge status={item.status} />
                <span className="rounded-md border border-purple-300/25 bg-purple-500/10 px-3 py-1.5 text-xl font-extrabold leading-none text-white">
                  {item.price || "-"}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-purple-400/15 pt-2 text-sm font-bold text-gray-400 md:block md:border-t-0 md:pt-0 md:text-right">
                <span className="md:hidden">Updated</span>
                <span className="truncate text-gray-200" title={formatDateTime(item.lastUpdatedAt)}>
                  {formatDateTime(item.lastUpdatedAt)}
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

function StatusDashboardSkeleton() {
  return (
    <div className="space-y-8" aria-hidden="true">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={`status-summary-skeleton-${index}`} className="rounded-lg border border-purple-400/20 bg-gray-950/60 p-4 md:p-5">
            <span className="skeleton-shimmer block h-5 w-2/3 rounded" />
            <span className="skeleton-shimmer mt-4 block h-9 w-16 rounded" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-purple-400/20 bg-gray-950/50 p-4 md:p-5">
        <span className="skeleton-shimmer block h-12 w-full rounded-lg" />
        <div className="mt-5 flex flex-wrap gap-3">
          {Array.from({ length: 5 }, (_, index) => (
            <span key={`status-filter-skeleton-${index}`} className="skeleton-shimmer h-10 w-28 rounded-lg" />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {Array.from({ length: 12 }, (_, index) => (
          <div key={`status-card-skeleton-${index}`} className="rounded-lg border border-purple-400/20 bg-gray-950/60 p-3">
            <div className="flex items-center gap-3">
              <span className="skeleton-shimmer h-12 w-12 rounded-md" />
              <span className="min-w-0 flex-1 space-y-2">
                <span className="skeleton-shimmer block h-5 w-4/5 rounded" />
                <span className="skeleton-shimmer block h-4 w-1/2 rounded" />
              </span>
              <span className="skeleton-shimmer hidden h-8 w-24 rounded-full md:block" />
              <span className="skeleton-shimmer hidden h-8 w-16 rounded-md md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GameStatusSummary({ items }) {
  const stats = items.reduce((totals, item) => {
    totals[item.status] = (totals[item.status] || 0) + 1;
    return totals;
  }, {});

  return (
    <div className="flex flex-wrap gap-2">
      {["undetected", "updating", "detected", "frozen"].map((status) => {
        const count = stats[status] || 0;
        if (count === 0) return null;

        return (
          <span key={status} className={`px-3 py-1.5 rounded-full border text-base font-bold ${STATUS_META[status].className}`}>
            {STATUS_META[status].label}: {count}
          </span>
        );
      })}
    </div>
  );
}

function GamesStatusSections({ groupedRows, searchTerm }) {
  const groups = Object.entries(groupedRows);

  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-purple-400/20 bg-gray-950/60 p-8 text-center text-gray-400 text-xl">
        No results found for "{searchTerm}".
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {groups.map(([gameTitle, items]) => (
        <section key={gameTitle} className="space-y-3">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 border-b border-purple-400/20 pb-3">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-purple-300">{gameTitle}</h3>
              <p className="text-gray-300 text-lg">{items.length} product{items.length > 1 ? "s" : ""}</p>
            </div>
            <GameStatusSummary items={items} />
          </div>

          <ProductStatusGrid rows={items} />
        </section>
      ))}
    </div>
  );
}

async function fetchGameData(game) {
  const response = await fetch(dataUrl(game.dataFile), { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const json = await response.json();
  const sortedItems = [...json].sort((a, b) => getStatusIndex(a.status) - getStatusIndex(b.status));

  return {
    gameId: game.id,
    gameTitle: game.name,
    data: sortedItems.map((item) => ({ ...item, gameId: game.id, gameTitle: game.name, gameImage: game.image, dataFile: game.dataFile })),
  };
}

export default function Status() {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [loadErrorCount, setLoadErrorCount] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCatalogScanning, setIsCatalogScanning] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadAllGames = async () => {
      const results = await Promise.all(
        ACTIVE_GAME_CATALOG.map(async (game) => {
          try {
            return await fetchGameData(game);
          } catch (error) {
            console.error(`Erreur lors du chargement de ${game.dataFile}:`, error);
            return null;
          }
        })
      );

      if (!isMounted) return;

      setLoadErrorCount(results.filter((result) => result === null).length);
      setRows(results.filter(Boolean).flatMap((result) => result.data));
      setLastUpdatedAt(new Date());
      setIsLoading(false);
    };

    loadAllGames();
    const intervalId = setInterval(loadAllGames, STATUS_REFRESH_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (isLoading) return undefined;
    setIsCatalogScanning(true);
    const timeoutId = setTimeout(() => setIsCatalogScanning(false), 420);
    return () => clearTimeout(timeoutId);
  }, [isLoading, searchTerm, selectedStatus]);

  const stats = useMemo(() => {
    return rows.reduce((totals, item) => {
      totals[item.status] = (totals[item.status] || 0) + 1;
      return totals;
    }, {});
  }, [rows]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    return rows
      .filter((item) => {
        const matchesSearch =
          item.name.toLowerCase().includes(normalizedSearchTerm) ||
          item.gameTitle.toLowerCase().includes(normalizedSearchTerm) ||
          String(item.price || "").toLowerCase().includes(normalizedSearchTerm) ||
          formatOs(item.os).toLowerCase().includes(normalizedSearchTerm) ||
          String(item.mode || "").toLowerCase().includes(normalizedSearchTerm) ||
          (Array.isArray(item.modes) && item.modes.some((mode) => String(mode).toLowerCase().includes(normalizedSearchTerm)));
        const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        const statusDiff = getStatusIndex(a.status) - getStatusIndex(b.status);
        if (statusDiff !== 0) return statusDiff;
        const gameDiff = a.gameTitle.localeCompare(b.gameTitle);
        return gameDiff || a.name.localeCompare(b.name);
      });
  }, [rows, normalizedSearchTerm, selectedStatus]);

  const groupedFilteredRows = useMemo(() => {
    return filteredRows.reduce((groups, item) => {
      if (!groups[item.gameTitle]) {
        groups[item.gameTitle] = [];
      }

      groups[item.gameTitle].push(item);
      return groups;
    }, {});
  }, [filteredRows]);

  return (
    <div className="relative min-h-screen flex flex-col bg-transparent text-white font-jersey overflow-hidden">
      <SeoMeta
        title="Status dashboard"
        description="Live product status dashboard with availability, price and Windows compatibility."
        canonicalPath="/Status"
      />
      <div className="relative z-10">
        <SiteNavbar animated={false} />

        <section className="pt-28 px-4 md:px-8 lg:px-10 py-12 bg-purple-950/25 backdrop-blur-[2px] border-b border-purple-400/20 rounded-b-3xl shadow-[0_18px_60px_rgba(124,58,237,0.18)]">
          <div className="max-w-7xl mx-auto space-y-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-3">
                  Status Dashboard
                </h2>
                <p className="text-gray-300 text-lg md:text-xl max-w-3xl">
                  Live product availability across all active games.
                </p>
              </div>

              <div className="bg-gray-950/60 border border-purple-400/20 rounded-lg px-4 py-3 text-gray-300">
                <div className="flex items-center gap-2 font-bold text-white">
                  <Activity size={18} className="text-purple-300" />
                  Last updated: {formatRefreshTime(lastUpdatedAt)}
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Auto-refresh every {Math.round(STATUS_REFRESH_INTERVAL_MS / 1000)}s
                </p>
              </div>
            </div>

            {isLoading && rows.length === 0 ? (
              <StatusDashboardSkeleton />
            ) : (
              <>
                <SummaryCards stats={stats} totalCount={rows.length} />

                <div className="bg-gray-950/50 border border-purple-400/20 rounded-lg p-4 md:p-5 space-y-5">
                  <SearchBar value={searchTerm} onChange={setSearchTerm} resultCount={filteredRows.length} />
                  <StatusFilters
                    selectedStatus={selectedStatus}
                    onSelect={setSelectedStatus}
                    stats={stats}
                    totalCount={rows.length}
                  />
                </div>

                {loadErrorCount > 0 && (
                  <p className="text-center text-yellow-300">
                    {loadErrorCount} data source{loadErrorCount > 1 ? "s" : ""} could not be loaded.
                  </p>
                )}

                <div className={`catalog-scan-surface ${isCatalogScanning ? "catalog-scan-active" : ""}`}>
                  <GamesStatusSections groupedRows={groupedFilteredRows} searchTerm={searchTerm} />
                </div>
              </>
            )}
          </div>
        </section>

        <SiteFooter />
      </div>
    </div>
  );
}
